import { translate } from '@jsverse/transloco';

import { Account } from 'app/providers/bridge-api/account.service';
import { SemantriaConfiguration } from 'app/providers/semantria-api/configuration.service';
import { Analysis } from 'app/providers/storage-api/analysis.service';
import { Collection } from 'app/providers/storage-api/collection.service';
import { CalculationListItem } from 'app/providers/storage-api/predictive.service';
import { Project } from 'app/providers/storage-api/project.service';
import { Upload, UploadState } from 'app/providers/storage-api/upload.service';

import { ObjectLabel, ItemLabel } from '../../../providers/storage-api/object-label.service';
import { DisplayItem, ProjectItem } from '../services/project-detail.service';
import { mapObjectLabelsToProjectItemLabels } from './common.model';

export enum AutoAnalysisAction {
  TurnOffAuto,
  TurnOnAuto,
  ShowAutoModal,
}

export class ProjectCollection {
  data: Collection;
  uploads: Upload[] = [];
  analyses: Analysis[] = [];
  driverAnalyses: CalculationListItem[] = [];

  constructor(collection: Collection) {
    this.data = collection;
    if (!this.data.indexedAt) {
      this.data.indexedAt = new Date();
    }
  }

  static createListFromProject(project: Project): ProjectCollection[] {
    const collectionMap: Map<string, ProjectCollection> = new Map();
    const collections = project.collections.map((collection) => {
      const cm = new ProjectCollection(collection);
      collectionMap[collection.collectionName] = cm;
      return cm;
    });
    project.uploads.forEach((upload) => {
      const cm: ProjectCollection = collectionMap[upload.collection_name];
      if (cm) {
        cm.addUpload(upload);
      }
    });
    project.analyses.forEach((analysis) => {
      const cm: ProjectCollection = collectionMap[analysis.collectionName];
      if (cm) {
        cm.addAnalysis(analysis);
      }
    });
    return collections;
  }

  static createProjectCollectionFromProject(
    project: Project,
    collection: Collection
  ): ProjectCollection {
    const pc = new ProjectCollection(collection);
    project.uploads.forEach((upload) => {
      if (pc) {
        pc.addUpload(upload);
      }
    });
    project.analyses.forEach((analysis) => {
      if (pc) {
        pc.addAnalysis(analysis);
      }
    });
    return pc;
  }

  static createCollectionItem(
    collection: ProjectCollection,
    configurations: Map<string, SemantriaConfiguration>,
    account: Account
  ): ProjectItem {
    const displayData: DisplayItem = {
      docCount: collection.data.documentCount,
      analysisCount: collection.data.analysisCount,
      isDriverAnalysis: collection.data.isDriverAnalysis,
      autoAnalysisButtonData: this.setAutoAnalysisButtonData(collection, configurations),
      hasLLMFeature: account?.features.includes('LARGE_LANGUAGE_MODELS'),
      columnsMapped: collection.columnsMapped,
      isLocked: collection.data.locked,
      activeUse: [],
    };
    return {
      type: 'collection',
      id: collection.data.collectionName,
      name: collection.data.displayName,
      modified: new Date(collection.data.indexedAt),
      created: new Date(collection.data.indexedAt),
      labels: this.createCollectionLabels(collection),
      displayData,
      collection,
      isSocial: collection.data.isSocial,
    };
  }

  static createCollectionLabels(collection: ProjectCollection): Partial<ItemLabel>[] {
    const labels = [];

    const aaLabel = ProjectCollection.createAALabel(collection);
    if (aaLabel) {
      labels.push(aaLabel);
    }

    const dTagLabel = ProjectCollection.createDTagLabel(collection);
    if (dTagLabel) {
      labels.push(dTagLabel);
    }

    const kdaLabel = ProjectCollection.createKDALabel(collection);
    if (kdaLabel) {
      labels.push(kdaLabel);
    }

    labels.push(
      ...mapObjectLabelsToProjectItemLabels(collection.data.objectLabels as ObjectLabel[])
    );

    return labels;
  }

  static createDTagLabel(collection: ProjectCollection): Partial<ItemLabel> {
    if (collection.data.isSocial) {
      return {
        label: 'D-TAG',
        tooltip: translate('project-collection-model.d-tag-analysis'),
        type: 'system',
      };
    }
  }

  static createAALabel(collection: ProjectCollection): Partial<ItemLabel> {
    if (collection?.data?.isAutoAnalyze) {
      return {
        label: 'AA',
        tooltip: translate('project-collection-model.auto-analysis'),
        type: 'system',
      };
    }
  }

  static createKDALabel(collection: ProjectCollection): Partial<ItemLabel> {
    if (collection.data.isDriverAnalysis) {
      return { label: 'KDA', tooltip: translate('project-collection-model.kda'), type: 'system' };
    }
  }

  static setAutoAnalysisButtonData(collection, configurations) {
    if (configurations) {
      let autoAnalysisTooltip = '';
      let autoAnalysisButtonText = '';
      let autoAnalysisAction: AutoAnalysisAction = AutoAnalysisAction.TurnOffAuto;
      if (
        !collection.canCreateAnalysis &&
        collection.uploads.some((upload) => !!upload.result.errors)
      ) {
        autoAnalysisTooltip = translate('project-collection-model.upload-error');
      }
      if (collection.data.isAutoAnalyze) {
        autoAnalysisButtonText = translate('project-collection-model.turn-off-aa');
        autoAnalysisAction = AutoAnalysisAction.TurnOffAuto;
      } else {
        autoAnalysisButtonText = translate('project-collection-model.set-to-aa');

        if (!collection.data.documentCount) {
          autoAnalysisTooltip = translate('project.project-details-table.coll-no-docs');
        } else if (collection.analyses.length === 0) {
          autoAnalysisAction = AutoAnalysisAction.ShowAutoModal;
        } else if (collection.analyses.length === 1) {
          autoAnalysisAction = AutoAnalysisAction.TurnOnAuto;
          const configId = collection.analyses[0] && collection.analyses[0].configId;

          if (
            configId &&
            !configId.startsWith('ip:') &&
            !configId.startsWith('lt:') &&
            !configId.startsWith('cr:')
          ) {
            const config = configurations.get(collection.analyses[0].configId);
            if (!config || config.removed) {
              autoAnalysisTooltip = translate('project-collection-model.deleted-tooltip');
            }
          }
        } else {
          autoAnalysisTooltip = translate('project-collection-model.cannot-run-tooltip');
        }
      }
      if (collection.data.locked) {
        autoAnalysisTooltip = translate('project-collection-model.collection-locked');
      }
      return {
        autoAnalysisTooltip,
        autoAnalysisButtonText,
        autoAnalysisAction,
      };
    }
  }

  static setDriverAnalysisTooltip(collection, account) {
    const currentDriverAnalysisBalance = account.balances.DRIVER_ANALYSES || 0;
    if (collection.data.locked) {
      return translate('project-collection-model.collection-locked');
    }
    if (currentDriverAnalysisBalance < 1) {
      return translate('project-collection-model.reached-driver-limit');
    }

    if (!collection.analyses.length) {
      return translate('project-collection-model.not-enough-analysis');
    }
  }

  get indexDate(): Date {
    return this.data.indexedAt;
  }

  get lastNotCancelledUpload(): Upload | null {
    const notCancelled = this.uploads.filter((u) => u.state !== UploadState.CANCELLED);
    if (notCancelled.length === 0) {
      return null;
    }

    return notCancelled[notCancelled.length - 1];
  }

  get indexState(): UploadState {
    let state = UploadState.COMPLETE;

    if (this.uploads.length > 0) {
      state = this.lastNotCancelledUpload?.state ?? UploadState.CANCELLED;
    }

    return state;
  }

  get canDeleteCollection() {
    return (
      this.indexState === UploadState.INSPECTED ||
      this.indexState === UploadState.COMPLETE ||
      this.indexState === UploadState.CANCELLED ||
      this.indexState === UploadState.ERROR
    );
  }

  get shouldShowCreateFirstAnalysis() {
    return (
      !this.data.isAutoAnalyze &&
      this.data.documentCount > 0 &&
      !this.analyzeState &&
      (!this.data.uploadCount || this.indexState !== UploadState.INSPECTED)
    );
  }

  get canCreateAnalysis() {
    return (
      !this.data.isAutoAnalyze &&
      !this.activeAnalysis &&
      this.analyses.length <= 10 &&
      this.data.documentCount > 0 &&
      !this.data.locked
    );
  }

  get documentsNeedAnalyze() {
    return (
      this.indexState === UploadState.COMPLETE &&
      ((!this.analyses.length && this.documentCount) ||
        (this.analyses.length && this.analyses[0].documentCount !== this.documentCount))
    );
  }

  get shouldShowCompleteUpload() {
    return this.indexState === UploadState.INSPECTED;
  }

  get upload(): Upload {
    return this.lastNotCancelledUpload ?? this.uploads[this.uploads.length - 1];
  }

  get activeUpload(): Upload {
    return (
      this.uploads.find((upload) => upload.state === 'MAPPED' || upload.state === 'STARTED') || null
    );
  }

  get filenames(): string {
    return [...this.uploads.map((item) => item.file_name)].join(', ');
  }

  get columnsMapped(): number {
    const lengths = this.uploads.map(
      (upload) => upload.metadata.columns.filter((c) => c.field_name).length
    );
    return Math.max(...lengths);
  }

  setUploads(uploads: Upload[]) {
    this.uploads = uploads;
  }

  addUpload(upload: Upload) {
    const idx = this.uploads.findIndex((u) => u.id === upload.id);
    if (idx !== -1) {
      this.uploads[idx] = upload;
    } else {
      this.uploads.push(upload);
    }
  }

  removeUpload(upload: Upload) {
    const idx = this.uploads.findIndex((u) => u.id === upload.id);
    if (idx !== -1) {
      this.uploads.splice(idx, 1);
    }
  }

  get analyzeState(): string {
    if (this.analyses.length === 0) {
      return undefined;
    }
    let state = 'COMPLETED';
    this.analyses.forEach((item: Analysis) => {
      switch (item.status) {
        case 'OPEN':
        case 'QUEUED':
        case 'DELETING':
          state = 'STARTED';
          return;
        case 'ERROR':
          state = 'ERROR';
          break;
      }
    });
    return state;
  }

  get activeAnalysis(): Analysis {
    return (
      this.analyses.find(
        (analysis) =>
          analysis.status === 'OPEN' ||
          analysis.status === 'QUEUED' ||
          analysis.status === 'DELETING'
      ) || null
    );
  }

  setAnalyses(analyses: Analysis[]) {
    this.analyses = analyses;
    this.sortAnalyses();
  }

  addAnalysis(analysis: Analysis) {
    const idx = this.analyses.findIndex((a) => a.name === analysis.name);
    if (idx !== -1) {
      this.analyses[idx] = analysis;
    } else {
      this.analyses.push(analysis);
    }
    this.sortAnalyses();
  }

  sortAnalyses() {
    this.analyses.sort(function (a: Analysis, b: Analysis) {
      if (a.createdAt < b.createdAt) {
        return 1;
      }
      if (a.createdAt > b.createdAt) {
        return -1;
      }
      return 0;
    });
  }

  get documentCount(): number {
    return this.data.documentCount;
  }

  hasUploads(): boolean {
    return this.uploads.length > 0;
  }
}
